import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { getResumenHistorico } from '../../../services/apiTransactions';
import { getUserByEmail } from '../../../services/apiUsers';
import { useAuth } from '../../../providers/AuthContext';

const ResumenBlock = ({ title, resumenData, isCollapsed, setIsCollapsed }) => {
  if (!resumenData) return null;

  return (
    <div className="mb-6 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h2 className="text-lg font-bold text-[#111827]">{title}</h2>
        <button className="text-gray-500 hover:text-[#10174c] focus:outline-none transition-colors">
          <svg
            className={`w-6 h-6 transform transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100 mt-6'}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Tarjeta: Total Abonos */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-center transition-shadow hover:shadow-md">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Abonos (+)</p>
          <p className="text-3xl font-bold text-emerald-600">
            {resumenData.kpis?.totalAbonos != null ? `$${resumenData.kpis.totalAbonos.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
          </p>
        </div>

        {/* Tarjeta: Total Cargos */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-center transition-shadow hover:shadow-md">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Cargos (-)</p>
          <p className="text-3xl font-bold text-rose-600">
            {resumenData.kpis?.totalCargos != null ? `$${resumenData.kpis.totalCargos.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
          </p>
        </div>

        {/* Tarjeta: Saldo Final (Color Corporativo) */}
        <div className="bg-[#10174c] p-6 rounded-xl shadow-md flex flex-col justify-center relative overflow-hidden transition-shadow hover:shadow-lg">
          {/* Elemento decorativo sutil en el fondo */}
          <div className="absolute -right-4 -top-4 w-24 h-24 bg-white opacity-5 rounded-full blur-2xl pointer-events-none"></div>
          <p className="text-sm font-medium text-blue-100 mb-1 relative z-10">Saldo Final</p>
          <p className="text-3xl font-bold text-white relative z-10">
            {resumenData.kpis?.saldoFinal != null ? `$${resumenData.kpis.saldoFinal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-[#111827] mb-4">Desglose por Socio</h3>
        <div className="overflow-x-auto max-h-[500px]">
          <table className="w-full text-sm text-left text-gray-600 relative">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-4 py-3">Socio</th>
                <th className="px-4 py-3">C.I.</th>
                <th className="px-4 py-3 text-right">Abonos (+)</th>
                <th className="px-4 py-3 text-right">Cargos (-)</th>
                <th className="px-4 py-3 text-right">Saldo Final</th>
              </tr>
            </thead>
            <tbody>
              {resumenData.resumenSocios?.map((socio, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{socio.nombre}</td>
                  <td className="px-4 py-3">{socio.cedula}</td>
                  <td className="px-4 py-3 text-right text-green-600 font-medium">
                    {socio.abonos != null ? `$${socio.abonos.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-right text-red-600 font-medium">
                    {socio.cargos != null ? `$${socio.cargos.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    {socio.saldo != null ? `$${socio.saldo.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </td>
                </tr>
              ))}
              {(!resumenData.resumenSocios || resumenData.resumenSocios.length === 0) && (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No hay datos de socios
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-semibold text-[#111827] mb-4">Desglose por Clasificación de Movimientos</h3>
        <div className="overflow-x-auto max-h-[500px]">
          <table className="w-full lg:w-2/3 xl:w-1/2 text-sm text-left text-gray-600 relative">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-4 py-3">Clasificación</th>
                <th className="px-4 py-3 text-right">Suma de Movimientos</th>
              </tr>
            </thead>
            <tbody>
              {resumenData.resumenClasificacion?.map((item, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{item.clasificacion}</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    {item.sumaMovimientos != null ? `$${item.sumaMovimientos.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </td>
                </tr>
              ))}
              {(!resumenData.resumenClasificacion || resumenData.resumenClasificacion.length === 0) && (
                <tr>
                  <td colSpan="2" className="px-4 py-8 text-center text-gray-500">
                    No hay datos de clasificación
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
};

const ResumenHistoricoWidget = forwardRef((props, ref) => {
  const [resumenDataConsolidado, setResumenDataConsolidado] = useState(null);
  const [resumenDataIndividual, setResumenDataIndividual] = useState(null);
  const [isCollapsedConsolidado, setIsCollapsedConsolidado] = useState(false);
  const [isCollapsedIndividual, setIsCollapsedIndividual] = useState(false);
  const { auth, userEmail } = useAuth();

  const fetchResumen = async () => {
    if (!auth?.token || !userEmail) return;

    try {
      const user = await getUserByEmail(userEmail, auth.token, true);
      // Condición estricta: solo ejecutar si el usuario tiene un familyGroupId
      if (!user || !user.familyGroupId) return;

      if (user.isFamilyLeader) {
        setIsCollapsedIndividual(true);
        if (user.familyGroupId) {
          const resConsolidado = await getResumenHistorico(auth.token, 'familyGroup', user.familyGroupId);
          setResumenDataConsolidado(resConsolidado);
        }
        if (user.cedula) {
          const resIndividual = await getResumenHistorico(auth.token, 'socio', user.cedula);
          setResumenDataIndividual(resIndividual);
        }
      } else {
        setIsCollapsedIndividual(false);
        if (user.cedula) {
          const resIndividual = await getResumenHistorico(auth.token, 'socio', user.cedula);
          setResumenDataIndividual(resIndividual);
        }
        setResumenDataConsolidado(null);
      }
    } catch (error) {
      console.error("Error al obtener resumen historico:", error);
    }
  };

  useEffect(() => {
    fetchResumen();
  }, [auth]);

  useImperativeHandle(ref, () => ({
    refresh: fetchResumen
  }));

  if (!resumenDataConsolidado && !resumenDataIndividual) return null;

  return (
    <>
      {resumenDataConsolidado && (
        <ResumenBlock 
          title="Resumen Histórico Consolidado" 
          resumenData={resumenDataConsolidado} 
          isCollapsed={isCollapsedConsolidado} 
          setIsCollapsed={setIsCollapsedConsolidado} 
        />
      )}
      {resumenDataIndividual && (
        <ResumenBlock 
          title="Resumen Histórico" 
          resumenData={resumenDataIndividual} 
          isCollapsed={isCollapsedIndividual} 
          setIsCollapsed={setIsCollapsedIndividual} 
        />
      )}
    </>
  );
});

export default ResumenHistoricoWidget;
